// A voxel density field of a head of hair, built from its strand segments.
//
// Real strand renderers get their depth from self-shadowing and from local
// occlusion inside the hair mass — Unreal voxelises the groom for exactly this
// (hair voxelization feeds its shadow and scattering terms). A sphere-shell
// stand-in was tried first and is wrong wherever the hair does not fill a
// uniform layer around the scalp: on top of the head the outermost strands LIE
// on the skull, so a shell model puts them at full depth and shades the lit
// crown dark. The volume follows the strands themselves, so it is right for a
// bun, a bob and a wig alike, and for a MetaHuman groom later on with no
// per-hairstyle knob.
//
// Pure: positions in, a Uint8 grid out. The strand material samples it as a
// 3D texture — a short ray march toward each light for transmittance, one tap
// at the shaded point for ambient occlusion (hairStrands.js).

/**
 * @param {Float32Array} positions per-SEGMENT (start xyz, end xyz), as
 *   hairToSegmentBuffers / segmentBuffers produce
 * @param {object} [o]
 * @param {number} [o.res=48]      voxels per axis
 * @param {number} [o.pad=0.04]    padding around the bounds, as a fraction of the largest extent
 * @param {number} [o.percentile=0.99]  the count that maps to 255 (clips a few over-dense voxels)
 * @param {boolean} [o.blur=true]  one 3x3x3 box blur, so a strand's shadow is a soft mass, not voxel steps
 * @returns {{ data: Uint8Array, res: number, min: number[], size: number[], voxel: number, filled: number, maxCount: number }}
 */
export function buildHairDensity(positions, { res = 48, pad = 0.04, percentile = 0.99, blur = true } = {}) {
  if (!(positions instanceof Float32Array) || positions.length % 6 !== 0) {
    throw new Error('hairDensity: positions must be a Float32Array of segment (start, end) triples')
  }
  const segments = positions.length / 6
  if (segments === 0) throw new Error('hairDensity: no segments')
  let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y = positions[i + 1], z = positions[i + 2]
    if (x < minX) minX = x; if (x > maxX) maxX = x
    if (y < minY) minY = y; if (y > maxY) maxY = y
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z
  }
  const extent = Math.max(maxX - minX, maxY - minY, maxZ - minZ) || 1
  const padding = extent * pad
  // A cube of voxels: one voxel size on every axis, so the ray-march step is a
  // single length and the field is not stretched along the long axis.
  const size = extent + 2 * padding
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2
  const min = [cx - size / 2, cy - size / 2, cz - size / 2]
  const voxel = size / res
  const counts = new Float32Array(res * res * res)
  // Each segment deposits at its midpoint; a segment is short next to a voxel
  // (a strand is resampled to ~10-70 points), so the midpoint is a fair sample.
  for (let s = 0; s < segments; s++) {
    const o = s * 6
    const x = (positions[o] + positions[o + 3]) * 0.5
    const y = (positions[o + 1] + positions[o + 4]) * 0.5
    const z = (positions[o + 2] + positions[o + 5]) * 0.5
    const ix = Math.min(res - 1, Math.max(0, Math.floor((x - min[0]) / voxel)))
    const iy = Math.min(res - 1, Math.max(0, Math.floor((y - min[1]) / voxel)))
    const iz = Math.min(res - 1, Math.max(0, Math.floor((z - min[2]) / voxel)))
    counts[ix + res * (iy + res * iz)] += 1
  }
  let field = counts
  if (blur) {
    field = new Float32Array(counts.length)
    for (let z = 0; z < res; z++) for (let y = 0; y < res; y++) for (let x = 0; x < res; x++) {
      let sum = 0, n = 0
      for (let dz = -1; dz <= 1; dz++) { const zz = z + dz; if (zz < 0 || zz >= res) continue
        for (let dy = -1; dy <= 1; dy++) { const yy = y + dy; if (yy < 0 || yy >= res) continue
          for (let dx = -1; dx <= 1; dx++) { const xx = x + dx; if (xx < 0 || xx >= res) continue
            sum += counts[xx + res * (yy + res * zz)]; n++
          } } }
      field[x + res * (y + res * z)] = sum / n
    }
  }
  // Normalise to the chosen percentile of the NON-EMPTY voxels, so the scale
  // does not depend on how much of the cube is air.
  const nonEmpty = []
  let maxCount = 0
  for (let i = 0; i < field.length; i++) { if (field[i] > 0) nonEmpty.push(field[i]); if (field[i] > maxCount) maxCount = field[i] }
  nonEmpty.sort((a, b) => a - b)
  const ref = nonEmpty.length ? nonEmpty[Math.min(nonEmpty.length - 1, Math.floor(nonEmpty.length * percentile))] : 1
  const data = new Uint8Array(field.length)
  for (let i = 0; i < field.length; i++) data[i] = Math.min(255, Math.round((field[i] / ref) * 255))
  return { data, res, min, size: [size, size, size], voxel, filled: nonEmpty.length, maxCount }
}

/** Density in 0..1 at a local-space point, nearest voxel; for tests and CPU-side checks. */
export function sampleHairDensity(field, x, y, z) {
  const { res, min, voxel, data } = field
  const ix = Math.floor((x - min[0]) / voxel), iy = Math.floor((y - min[1]) / voxel), iz = Math.floor((z - min[2]) / voxel)
  if (ix < 0 || iy < 0 || iz < 0 || ix >= res || iy >= res || iz >= res) return 0
  return data[ix + res * (iy + res * iz)] / 255
}
